<template>
	<div class="cassie-vertical-md">
		<SectionCard>
			<template #title>
				Brands
			</template>
			<template #body>
				<v-container fluid>
					<v-row dense>
						<v-col
							cols="4"
							class="py-0"
						>
							<TextField
								:value.sync="configuration.basicConfig.brandFieldLabel"
								:label="'Brand Label *' | useLabels"
								style="width: 80%;"
								:disabled="!userFullPermissions"
								@input="updateBasicConfig('brandFieldLabel', $event)"
							/>
						</v-col>
						<v-col
							cols="6"
							class="py-0"
						>
							<Toggle
								:disabled="!userFullPermissions"
								:value.sync="configuration.basicConfig.onlyShowDataSubjectsAssociatedWithSelectedBrand"
								label="Only Show Data Subjects Associated With CS Portal User's Selected Brand"
								@update:value="updateBasicConfig('onlyShowDataSubjectsAssociatedWithSelectedBrand', $event)"
							/>
						</v-col>
					</v-row>
				</v-container>
			</template>
		</SectionCard>
		<SectionCard>
			<template #title>
				Portal Configuration
			</template>
			<template #body>
				<v-container fluid>
					<v-row
						class="align-center"
					>
						<v-col
							cols="4"
						>
							<Toggle
								:disabled="!userFullPermissions"
								:value.sync="configuration.basicConfig.canCreateDataSubjects"
								label="Can Create Data Subject"
								@update:value="updateBasicConfig('canCreateDataSubjects', $event)"
							/>
						</v-col>
						<v-col
							cols="4"
						>
							<Toggle
								:disabled="!userFullPermissions"
								:value.sync="configuration.basicConfig.canCreateCSPortalUsers"
								label="Can Create CS Portal Users"
								@update:value="updateBasicConfig('canCreateCSPortalUsers', $event)"
							/>
						</v-col>
					</v-row>
					<v-row
						class="align-center"
					>
						<v-col
							cols="4"
						>
							<Toggle
								:disabled="!userFullPermissions"
								:value.sync="configuration.basicConfig.canEditPreferences"
								label="Can Edit Preferences"
								@update:value="updateBasicConfig('canEditPreferences', $event)"
							/>
						</v-col>
						<v-col
							cols="4"
						>
							<Toggle
								:disabled="!userFullPermissions"
								:value.sync="configuration.basicConfig.canEditContactDetails"
								label="Can Edit Contact Details"
								@update:value="updateBasicConfig('canEditContactDetails', $event)"
							/>
						</v-col>
					</v-row>
				</v-container>
			</template>
		</SectionCard>
		<SectionCard v-if="configuration.basicConfig.canCreateDataSubjects || configuration.basicConfig.canEditPreferences">
			<template #title>
				Edit Preference Options
			</template>
			<template #body>
				<div class="cassie-vertical-md">
					<v-simple-table>
						<template #default>
							<thead>
								<tr>
									<th
										class="text-left"
										style="width: 20%;"
										scope="col"
									>
										Field
									</th>
									<th
										class="text-left"
										style="width: 15%;"
										scope="col"
									>
										Visible
									</th>
									<th
										class="text-left"
										style="width: 15%;"
										scope="col"
									>
										Is Mandatory
									</th>
									<th
										class="text-left"
										style="width: 25%;"
										scope="col"
									>
										Label
									</th>
									<th
										class="text-left"
										style="width: 25%;"
										scope="col"
									>
										<span>
											Allow user input
											<v-tooltip
												slot="append"
												right
											>
												<template #activator="{ on }">
													<v-icon
														light
														class="ml-2"
														v-on="on"
													>
														mdi-information
													</v-icon>
												</template>
												<div v-dompurify-html="'If turned on, this allows a CSP user to select \'Other\' and input their own value which will be submitted along with the preferences'" />
											</v-tooltip>
										</span>
									</th>
									<th
										class="text-center"
										style="width: 80px;"
										scope="col"
									>
										Action
									</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td class="text-left">
										Response Channel
									</td>
									<td>
										<Toggle
											:disabled="!userFullPermissions"
											:value.sync="configuration.basicConfig.showResponseChannelForPrefSubmissions"
											class="mt-0"
											hide-details
											@update:value="updateBasicConfig('showResponseChannelForPrefSubmissions', $event)"
										/>
									</td>
									<td>
										<Toggle
											:disabled="!userFullPermissions"
											:value.sync="configuration.basicConfig.responseChannelForSubmissionsMandatory"
											class="mt-0"
											hide-details
											@update:value="updateBasicConfig('responseChannelForSubmissionsMandatory', $event)"
										/>
									</td>
									<td>
										<TextField
											:disabled="!userFullPermissions"
											:value.sync="configuration.basicConfig.responseChannelFieldLabel"
											style="width: 80%;"
											:rules="{max:60}"
											@input="updateBasicConfig('responseChannelFieldLabel', $event)"
										/>
									</td>
									<td>
										<Toggle
											:disabled="!userFullPermissions"
											:value.sync="configuration.basicConfig.responseChannelAllowUserInput"
											class="mt-2"
											@update:value="updateBasicConfig('responseChannelAllowUserInput', $event)"
										/>
									</td>
									<td
										v-if="userFullPermissions"
										class="text-center"
									>
										<v-menu
											bottom
											left
										>
											<template #activator="{ on, attrs }">
												<v-btn
													icon
													v-bind="attrs"
													v-on="on"
													@click.stop
												>
													<v-icon>
														mdi-dots-horizontal
													</v-icon>
												</v-btn>
											</template>
											<v-list dense>
												<v-list-item @click="showResponseChannelsModal = true">
													<v-list-item-content>
														<v-list-item-title>
															Manage Response Channels
														</v-list-item-title>
													</v-list-item-content>
												</v-list-item>
											</v-list>
										</v-menu>
									</td>
								</tr>
								<tr>
									<td class="text-left">
										Source Code
									</td>
									<td>
										<Toggle
											:disabled="!userFullPermissions"
											:value.sync="configuration.basicConfig.showSourceCodeForPrefSubmissions"
											class="mt-0"
											hide-details
											@update:value="updateBasicConfig('showSourceCodeForPrefSubmissions', $event)"
										/>
									</td>
									<td>
										<Toggle
											:disabled="!userFullPermissions"
											:value.sync="configuration.basicConfig.sourceCodeForSubmissionsMandatory"
											class="mt-0"
											hide-details
											@update:value="updateBasicConfig('sourceCodeForSubmissionsMandatory', $event)"
										/>
									</td>
									<td class="text-left">
										<TextField
											:disabled="!userFullPermissions"
											:value.sync="configuration.basicConfig.sourceCodeFieldLabel"
											style="width: 80%;"
											:rules="{max:60}"
											@input="updateBasicConfig('sourceCodeFieldLabel', $event)"
										/>
									</td>
									<td>
										<Toggle
											:disabled="!userFullPermissions"
											:value.sync="configuration.basicConfig.sourceCodeAllowUserInput"
											class="mt-2"
											@update:value="updateBasicConfig('sourceCodeAllowUserInput', $event)"
										/>
									</td>
									<td
										v-if="userFullPermissions"
										class="text-center"
									>
										<v-menu
											bottom
											left
										>
											<template #activator="{ on, attrs }">
												<v-btn
													icon
													v-bind="attrs"
													v-on="on"
													@click.stop
												>
													<v-icon>
														mdi-dots-horizontal
													</v-icon>
												</v-btn>
											</template>
											<v-list dense>
												<v-list-item @click="showSourceCodesModal = true">
													<v-list-item-content>
														<v-list-item-title>
															Manage Source Codes
														</v-list-item-title>
													</v-list-item-content>
												</v-list-item>
											</v-list>
										</v-menu>
									</td>
								</tr>
							</tbody>
						</template>
					</v-simple-table>
				</div>
			</template>
		</SectionCard>
		<SectionCard>
			<template #title>
				Channel Options
			</template>
			<template #body>
				<v-container fluid>
					<div class="text-subtitle-2 mb-3">
						Manage valid from and to dates behaviour settings
					</div>
					<v-row class="align-start mb-2">
						<v-col
							cols="12"
							xl="6"
							lg="6"
							md="12"
						>
							<v-row class="align-center mb-2">
								<v-col
									cols="12"
									class="d-flex align-center"
								>
									<span
										class="text-body-2 mr-4"
										style="min-width: 90px; display: inline-block;"
									>
										Valid From:
									</span>
									<div class="d-flex align-center mr-6 mr-lg-10">
										<Toggle
											:disabled="!userFullPermissions"
											:value.sync="configuration.basicConfig.showValidFromFieldForPrefSubmissions"
											label="View"
											hide-details
											@update:value="handleValidFromViewToggle($event)"
										/>
									</div>
									<div class="d-flex align-center">
										<Toggle
											:disabled="!userFullPermissions || !configuration.basicConfig.showValidFromFieldForPrefSubmissions"
											:value.sync="configuration.basicConfig.canEditValidFromFieldForPrefSubmissions"
											label="Edit"
											hide-details
											@update:value="updateBasicConfig('canEditValidFromFieldForPrefSubmissions', $event)"
										/>
									</div>
								</v-col>
							</v-row>
							<v-row class="align-center mb-2">
								<v-col
									cols="12"
									class="d-flex align-center"
								>
									<span
										class="text-body-2 mr-4"
										style="min-width: 90px; display: inline-block;"
									>
										Valid To:
									</span>
									<div class="d-flex align-center mr-6 mr-lg-10">
										<Toggle
											:disabled="!userFullPermissions"
											:value.sync="configuration.basicConfig.showValidToFieldForPrefSubmissions"
											label="View"
											hide-details
											@update:value="handleValidToViewToggle($event)"
										/>
									</div>
									<div class="d-flex align-center">
										<Toggle
											:disabled="!userFullPermissions || !configuration.basicConfig.showValidToFieldForPrefSubmissions"
											:value.sync="configuration.basicConfig.canEditValidToFieldForPrefSubmissions"
											label="Edit"
											hide-details
											@update:value="updateBasicConfig('canEditValidToFieldForPrefSubmissions', $event)"
										/>
									</div>
								</v-col>
							</v-row>
						</v-col>
						<v-col
							cols="12"
							xl="6"
							lg="6"
							md="12"
							class="d-flex align-start"
						>
							<Toggle
								:disabled="!userFullPermissions"
								:value.sync="configuration.basicConfig.privacyPolicyMandatory"
								label="Privacy Policy Mandatory"
								@update:value="updateBasicConfig('privacyPolicyMandatory', $event)"
							/>
						</v-col>
					</v-row>
				</v-container>
			</template>
		</SectionCard>
		<SectionCard>
			<template #title>
				Additional Options
			</template>
			<template #body>
				<v-container fluid>
					<v-row class="align-center">
						<v-col cols="6">
							<v-row class="align-center">
								<v-col cols="8">
									<Toggle
										:disabled="!userFullPermissions"
										:value.sync="configuration.basicConfig.dataSubjectsWithNoPreferencesTickedByDefault"
										label="'Display Data Subjects With No Preferences' Checkbox Selected by Default"
										@update:value="updateBasicConfig('dataSubjectsWithNoPreferencesTickedByDefault', $event)"
									/>
								</v-col>
								<v-col cols="4" />
							</v-row>
						</v-col>
						<v-col cols="6">
							<v-row class="align-center">
								<v-col cols="6">
									<Toggle
										:disabled="!userFullPermissions"
										:value.sync="configuration.basicConfig.auditHistorySortNewestToOldest"
										label="Sort Submission History Newest to Oldest"
										@update:value="updateBasicConfig('auditHistorySortNewestToOldest', $event)"
									/>
								</v-col>
								<v-col cols="6">
									<TextField
										:disabled="!userFullPermissions"
										:value.sync="configuration.basicConfig.auditHistoryExpandedByDefault"
										type="number"
										class="cassie-input-width-md"
										label="Submission History Expanded Items *"
										tooltip-text="Specify how many submission history items are expanded by default"
										:rules="{required:true, numeric: true}"
										@input="updateBasicConfig('auditHistoryExpandedByDefault', $event)"
									/>
								</v-col>
							</v-row>
						</v-col>
					</v-row>
					<v-row>
						<v-col
							cols="4"
						>
							<TextField
								:disabled="!userFullPermissions"
								:value.sync="configuration.basicConfig.customLogoutUrl"
								:rules="{max: 255}"
								label="Custom logout url"
								@input="updateBasicConfig('customLogoutUrl', $event)"
							/>
						</v-col>
					</v-row>
				</v-container>
			</template>
		</SectionCard>
		<CsPortalConfigResponseChannelsModal
			v-if="showResponseChannelsModal"
			:response-channels="responseChannels"
			@close="showResponseChannelsModal = false"
			@update:responseChannels="updateResponseChannels($event)"
		/>
		<CsPortalConfigSourceCodesModal
			v-if="showSourceCodesModal"
			:source-codes="sourceCodes"
			@close="showSourceCodesModal = false"
			@update:sourceCodes="updateConfiguration('sourceCodes', $event)"
		/>
	</div>
</template>

<script>
import SectionCard from '../../../../../../shared/components/section-card.vue'
import TextField from '../../../../../../shared/components/text-field.vue'
import Toggle from '../../../../../../shared/components/toggle.vue'
import CsPortalConfigResponseChannelsModal from './cs-portal-config-response-channels-modal.vue'
import CsPortalConfigSourceCodesModal from './cs-portal-config-source-codes-modal.vue'
import { postIpWhitelist, getIpWhitelists, deleteIpWhitelist } from '../../../../../../shared/utils/api/cs-portal-config-whitelisting.js'
export default {
	components: {
		CsPortalConfigResponseChannelsModal,
		CsPortalConfigSourceCodesModal,
		TextField,
		Toggle,
		SectionCard
	},
	props: {
		configuration: {
			type: Object,
			default: () => ({})
		},
		responseChannels: {
			type: Array,
			default: () => []
		},
		sourceCodes: {
			type: Array,
			default: () => []
		},
		userFullPermissions: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			showResponseChannelsModal: false,
			showSourceCodesModal: false
		}
	},
	methods: {
		postIpWhitelist,
		getIpWhitelists,
		deleteIpWhitelist,
		updateResponseChannels (responseChannels) {
			this.updateConfiguration('responseChannels', responseChannels)
		},
		updateSourceCodes (sourceCodes) {
			this.updateConfiguration('sourceCodes', sourceCodes)
		},
		updateBasicConfig (key, value) {
			const basicConfig = {
				...this.configuration.basicConfig,
				[key]: value
			}
			this.updateConfiguration('basicConfig', basicConfig)
		},
		handleValidFromViewToggle (value) {
			this.updateBasicConfig('showValidFromFieldForPrefSubmissions', value)
			if (!value && this.configuration.basicConfig.canEditValidFromFieldForPrefSubmissions) {
				this.updateBasicConfig('canEditValidFromFieldForPrefSubmissions', false)
			}
		},
		handleValidToViewToggle (value) {
			this.updateBasicConfig('showValidToFieldForPrefSubmissions', value)
			if (!value && this.configuration.basicConfig.canEditValidToFieldForPrefSubmissions) {
				this.updateBasicConfig('canEditValidToFieldForPrefSubmissions', false)
			}
		},
		updateConfiguration (key, value) {
			const configuration = {
				...this.configuration,
				[key]: value
			}
			this.$emit('update-configuration', configuration)
		}
	}
}
</script>
